import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Layout,
  Menu,
  Row,
  Col,
  Typography,
  Card,
  Avatar,
  Select,
  Button,
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import {
  EnvironmentOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InstagramOutlined,
  PlusOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { setPortalSelectedBranch } from '../../actions';
import LogoEmpresa from '../../assets/img/a-iso-blanco-8.png';
import Footer from '../../components/Footer';
import NoAvatar from '../../assets/img/no-avatar-iso.jpg';
import { getUsersTypeApi } from '../../api/user';
import './Services.scss';
import '../../components/DoctorCard/DoctorCard.scss';
import Spin from '../../components/Spin';
import { sortProfessionalsForPortal } from '../../utils/sortProfessionals';
import { matchesSpecialityWord } from '../../utils/matchSpecialityWord';
import { filterByPortalBranch } from '../../utils/portalHomeBranchFilter';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;
const PortalHome = () => {
  const [professionals, setProfessionals] = useState([]);
  const [medicalSpecialities, setMedicalSpecialities] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [viewCardInfo, setViewCardInfo] = useState({});
  const dispatch = useDispatch();

  // Referencias a los componentes
  const inicioRef = useRef(null);
  const profesionalesRef = useRef(null);

  const handleScroll = (ref) => {
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const {
    tenant,
    organization,
    tenantLogo,
    direction,
    branches,
    organizationPhone,
    tenantId,
    studies,
    instagram,
    organizationDescription,
    portalActive,
    tenantTimezone,
    versionPortal,
  } = useSelector((store) => store.tenantData);
  const selectedBranchIdFromStore = useSelector(
    (store) => store.portalFilters?.selectedBranchId,
  );

  // Inicializar filtro de sede desde Redux (persistido)
  useEffect(() => {
    try {
      const branchId = selectedBranchIdFromStore;
      if (branchId) {
        setSelectedBranch(String(branchId));
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persistir el filtro de sede en Redux (para "recordarla" al navegar)
  useEffect(() => {
    try {
      dispatch(setPortalSelectedBranch(selectedBranch));
    } catch {
      // ignore
    }
  }, [selectedBranch, dispatch]);

  useEffect(() => {
    const getUsers = async () => {
      try {
        setLoading(true);
        const userResponse = await getUsersTypeApi(tenantId);

        // Extraer medicalSpeciality y filtrar únicos
        const uniqueSpecialities = [
          ...new Set(
            userResponse.users
              .map((user) => user.medicalSpeciality)
              .filter(Boolean),
          ),
        ];

        setProfessionals(userResponse.users);
        setMedicalSpecialities(uniqueSpecialities);
      } catch {
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, []);

  const Detail = (professional, type) => {
    const professionalId =
      type === 'studie' ? professional.id : professional?._id;
    const isExpanded = expandedCards[professionalId] || false;

    const toggleExpand = () => {
      setExpandedCards((prev) => ({
        ...prev,
        [professionalId]: !prev[professionalId],
      }));
    };

    const formatTime = (date) => dayjs(date).tz(tenantTimezone).format('HH:mm');

    const getBranchName = (branchId) => {
      if (!branches || branches.length < 2) return null;
      const branch = branches.find((b) => b._id === branchId);
      return branch ? branch.name : null;
    };

    const Schedule = (hours) => {
      if (!hours || hours.length === 0) {
        return (
          <div>
            <p style={{ fontSize: '8pt', color: '#888', fontStyle: 'italic' }}>
              Este profesional no cuenta con horarios fijos. Los turnos se
              habilitan periódicamente según su disponibilidad.
            </p>
          </div>
        );
      }

      const visibleHours = isExpanded ? hours : hours?.slice(0, 2);

      return !viewCardInfo[professionalId] ? (
        <div>
          <span style={{ fontSize: '8pt', color: '#888' }}>
            🗓️ Horarios habituales:
          </span>
          {professional?.scheduleConfig?.quantityDaysPortal === 0 ? (
            <span
              style={{
                fontSize: '8pt',
                fontWeight: 600,
                color: '#1890ff',
                display: 'block',
                marginTop: 2,
                marginBottom: 2,
              }}
            >
              Este profesional trabaja por orden de llegada
            </span>
          ) : null}
          <div>
            {visibleHours?.map(
              ({ start, end, days, branchId, address }, idx) => {
                // Mostrar info de sede (si tiene branchId esa sede; si no, sede principal por defecto)
                let locationInfo = '';
                if (
                  versionPortal === 'premium' &&
                  branches &&
                  branches.length >= 2
                ) {
                  const branchName = branchId ? getBranchName(branchId) : null;
                  const mainBranch =
                    branches.find((b) => b.isMain) || branches[0];
                  const defaultBranchName =
                    mainBranch?.name || 'Sede principal';
                  if (branchName) {
                    locationInfo = (
                      <span style={{ color: '#1890ff', fontWeight: '500' }}>
                        {' '}
                        • {branchName}
                      </span>
                    );
                  } else {
                    locationInfo = (
                      <span style={{ color: '#666', fontStyle: 'italic' }}>
                        {' '}
                        • {defaultBranchName}
                      </span>
                    );
                  }
                }

                return (
                  <p
                    key={idx}
                    style={{ fontSize: '7pt', color: '#888', margin: '4px 0' }}
                  >
                    ⏰ {days && days.join(', ').toUpperCase()}:{' '}
                    {formatTime(start)} hs - {formatTime(end)} hs
                    {locationInfo}
                  </p>
                );
              },
            )}
          </div>

          {hours?.length > 2 && (
            <Button
              onClick={toggleExpand}
              type="text"
              className="toggle-button"
            >
              {isExpanded ? 'Ver menos' : 'Ver más'}
            </Button>
          )}
        </div>
      ) : (
        <div>
          <span style={{ fontSize: '8pt', display: 'block', color: '#888' }}>
            🗒️ Información:{' '}
          </span>
          <span style={{ fontSize: '8pt', color: '#888' }}>
            {professional?.scheduleConfig?.description}
          </span>
        </div>
      );
    };

    return (
      <Card
        style={{
          borderRadius: 10,
          backgroundColor: '#f0f2f5',
          margin: '5px 0',
        }}
        actions={[
          professional?.scheduleConfig?.description === '' &&
          professional?.scheduleConfig?.quantityDaysPortal === 0 ? (
            <span>
              <EyeInvisibleOutlined /> Sin Información
            </span>
          ) : professional?.scheduleConfig?.quantityDaysPortal === 0 ? (
            <div
              style={{ width: '100%', height: '100%' }}
              onClick={() =>
                setViewCardInfo((prev) => ({
                  ...prev,
                  [professionalId]: !prev[professionalId],
                }))
              }
            >
              <span>
                <EyeOutlined className="button" />{' '}
                {!viewCardInfo[professionalId]
                  ? 'Ver Información'
                  : 'Ver Horarios'}
              </span>
            </div>
          ) : (
            <Link
              to={`/turnos/${
                type === 'studie' ? professional.id : professional?._id
              }/${type}`}
              className="button"
            >
              <span>
                <PlusOutlined className="button" /> Solicitar Turno
              </span>
            </Link>
          ),
        ]}
        hoverable
        className="doctor-card-custom"
      >
        <div className="doctor-card-content">
          <div className="doctor-card-header">
            <Avatar
              alt="Foto perfil"
              src={
                professional?.profileImage
                  ? `https://storage.googleapis.com/bs-proyecto1.appspot.com/${tenant}/profiles/${professional?.profileImage}`
                  : NoAvatar
              }
              className="doctor-avatar"
              size={100}
            />
            <div className="doctor-info">
              <h3 className="doctor-name">
                {professional.name} {professional?.lastname}
              </h3>
              <p className="doctor-speciality">
                {professional?.medicalSpeciality}
              </p>
            </div>
          </div>

          <div className="doctor-schedule">
            {Schedule(professional?.scheduleConfig?.hours)}
          </div>
        </div>
      </Card>
    );
  };

  const branchFilterContext = useMemo(
    () => ({ versionPortal, selectedBranch, branches }),
    [versionPortal, selectedBranch, branches],
  );

  const professionalsByBranch = useMemo(
    () => filterByPortalBranch(professionals, branchFilterContext),
    [professionals, branchFilterContext],
  );

  const studiesByBranch = useMemo(
    () => filterByPortalBranch(studies, branchFilterContext),
    [studies, branchFilterContext],
  );

  // Opciones combinadas para el Select: especialidades y estudios (estudios respetan sede)
  const specialityOptions = useMemo(
    () => [
      ...medicalSpecialities.map((speciality) => ({
        value: speciality,
        label: speciality,
        type: 'speciality',
      })),
      ...studiesByBranch.map((study) => ({
        value: `study-${study.id || study._id || study.name}`,
        label: `Estudio: ${study.name}`,
        type: 'study',
        studyId: study.id || study._id || study.name,
      })),
    ],
    [medicalSpecialities, studiesByBranch],
  );

  // Filtrar por palabra completa: "urologia" incluye "urologia + X", no "neurologia"
  const professionalsBySpeciality = useMemo(() => {
    if (selectedSpeciality && !selectedSpeciality.startsWith('study-')) {
      return professionalsByBranch.filter((p) =>
        matchesSpecialityWord(p.medicalSpeciality, selectedSpeciality),
      );
    }
    return professionalsByBranch;
  }, [professionalsByBranch, selectedSpeciality]);

  const sortedProfessionalsBySpeciality = useMemo(
    () => sortProfessionalsForPortal(professionalsBySpeciality),
    [professionalsBySpeciality],
  );

  // Filtrar profesional seleccionado
  const filteredProfessionals = selectedProfessional
    ? sortedProfessionalsBySpeciality.filter(
        (p) => `${p.name} ${p.lastname}` === selectedProfessional,
      )
    : sortedProfessionalsBySpeciality;

  const filteredStudies = useMemo(() => {
    if (selectedProfessional) {
      return [];
    }
    if (selectedSpeciality?.startsWith('study-')) {
      return studiesByBranch.filter(
        (study) =>
          `study-${study.id || study._id || study.name}` === selectedSpeciality,
      );
    }
    if (!selectedSpeciality) {
      return studiesByBranch;
    }
    return [];
  }, [
    selectedProfessional,
    selectedSpeciality,
    studiesByBranch,
  ]);

  // Resetear filtros
  const resetFilters = () => {
    setSelectedSpeciality(null);
    setSelectedProfessional(null);
    setSelectedBranch(null);
  };

  const Professional = ({ professionals }) => (
    <div>
      <div className="portal-home-filters">
        <div className="portal-home-filters__card">
            <h4 className="portal-home-filters__title">Buscar profesionales</h4>
            <p className="portal-home-filters__desc">
              Filtrá por sede, especialidad o nombre para encontrar a quien
              necesitás.
            </p>
            <Row gutter={[16, 16]} align="bottom">
              {versionPortal === 'premium' &&
                branches &&
                branches.length >= 2 && (
                  <Col xs={24} sm={12} md={6}>
                    <label className="portal-home-filters__label">Sede</label>
                    <Select
                      placeholder="Todas las sedes"
                      className="portal-home-filters__select"
                      value={selectedBranch}
                      onChange={(value) => {
                        setSelectedBranch(value ? String(value) : null);
                        setSelectedProfessional(null);
                      }}
                      allowClear
                    >
                      {branches.map((branch) => (
                        <Select.Option key={branch._id} value={branch._id}>
                          {branch.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                )}
              <Col
                xs={24}
                sm={12}
                md={
                  versionPortal === 'premium' &&
                  branches &&
                  branches.length >= 2
                    ? 6
                    : 8
                }
              >
                <label className="portal-home-filters__label">
                  Especialidad o estudio
                </label>
                <Select
                  placeholder="Especialidad o agenda de estudio"
                  className="portal-home-filters__select"
                  value={selectedSpeciality}
                  onChange={(value) => {
                    setSelectedSpeciality(value);
                    setSelectedProfessional(null);
                  }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                >
                  {specialityOptions.map((option, idx) => (
                    <Select.Option
                      key={idx}
                      value={option.value}
                      label={option.label}
                    >
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col
                xs={24}
                sm={12}
                md={
                  versionPortal === 'premium' &&
                  branches &&
                  branches.length >= 2
                    ? 6
                    : 8
                }
              >
                <label className="portal-home-filters__label">
                  Profesional
                </label>
                <Select
                  placeholder="Buscar por nombre o seleccionar"
                  className="portal-home-filters__select"
                  value={selectedProfessional}
                  onChange={setSelectedProfessional}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes((input || '').toLowerCase())
                  }
                  disabled={
                    (selectedSpeciality &&
                      selectedSpeciality.startsWith('study-')) ||
                    (!selectedSpeciality && !professionals.length)
                  }
                >
                  {sortedProfessionalsBySpeciality.map((professional, idx) => (
                    <Select.Option
                      key={idx}
                      value={`${professional.name} ${professional.lastname}`}
                      label={`${professional.name} ${professional.lastname} - ${professional.medicalSpeciality ?? ''}`}
                    >
                      {professional.name} {professional.lastname} - {professional.medicalSpeciality ?? ''}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col
                xs={24}
                sm={12}
                md={
                  versionPortal === 'premium' &&
                  branches &&
                  branches.length >= 2
                    ? 6
                    : 8
                }
                className="portal-home-filters__actions"
              >
                <Button
                  type="default"
                  onClick={resetFilters}
                  block
                  className="portal-home-filters__reset"
                >
                  Limpiar filtros
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      <Row gutter={[16, 16]}>
        {/* Mostrar profesionales filtrados por sede + especialidad (y opcionalmente por nombre) */}
        {(!selectedSpeciality || !selectedSpeciality.startsWith('study-')) &&
          filteredProfessionals.map((professional, index) => (
            <Col key={index} xs={24} sm={12} lg={8}>
              {Detail(professional, 'medico')}
            </Col>
          ))}
        {/* Mostrar solo la agenda de estudio seleccionada si corresponde */}
        {filteredStudies.map((studie, index) => (
          <Col key={index} xs={24} sm={12} lg={8}>
            {Detail(studie, 'studie')}
          </Col>
        ))}
      </Row>
    </div>
  );

  return (
    <Layout className="portal-home">
      {/* Header */}
      <Header>
        {/* Menú de navegación */}
        <Menu mode="horizontal">
          <Menu.Item key="1">
            <Avatar
              alt="Foto perfil"
              src={LogoEmpresa}
              style={{
                width: '30px',
                height: '30px',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
          </Menu.Item>
          {portalActive && (
            <Menu.Item
              key="4"
              style={{
                marginLeft: 'auto',
                fontWeight: 'bold',
              }}
            >
              <Link to="/login">Mis estudios</Link>
            </Menu.Item>
          )}
          <Menu.Item key="2" onClick={() => handleScroll(profesionalesRef)}>
            Profesionales
          </Menu.Item>
        </Menu>
      </Header>

      {/* Content */}
      <Content ref={inicioRef}>
        <div className="portal-hero">
          <div className="center-header-section">
            {/* Columna izquierda: logo + nombre + bienvenida */}
            <div className="hero-left">
              <div className="center-avatar-container">
                <div className="logo-circle">
                  <img
                    src={`https://storage.googleapis.com/bs-proyecto1.appspot.com/${tenant}/${
                      tenantLogo || 'logo'
                    }.png`}
                    alt="Logo"
                    className="logo-img"
                  />
                </div>
              </div>
              <div className="hero-text">
                <Title level={3} className="center-title">
                  {organization}
                </Title>
                <div className="welcome-section">
                  <Title level={2} className="welcome-title">
                    Bienvenidos
                  </Title>
                  <Paragraph className="welcome-description">
                    {organizationDescription}
                  </Paragraph>
                </div>
              </div>
            </div>

            {/* Columna derecha: contacto o sedes */}
            <div className="hero-right">
              {versionPortal === 'premium' &&
              branches &&
              branches.length >= 2 ? (
                <div className="branches-section">
                  <h4 className="branches-title">Nuestras sedes</h4>
                  <div className="branches-list">
                    {branches.map((branch) => (
                      <div
                        key={branch._id}
                        className={`branch-item ${branch.isMain ? 'branch-main' : ''}`}
                      >
                        <div className="branch-header">
                          <span className="branch-name">{branch.name}</span>
                          {branch.isMain && (
                            <span className="branch-badge">Principal</span>
                          )}
                        </div>
                        <div className="branch-address">
                          <EnvironmentOutlined />
                          <span>{branch.address}</span>
                        </div>
                        {branch.phone && (
                          <div className="branch-phone">
                            <WhatsAppOutlined />
                            <a
                              href={`https://wa.me/${branch.phone}?text=Hola%20${organization}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              referrerPolicy="origin"
                            >
                              {branch.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {(organizationPhone || instagram) && (
                    <div className="hero-contact-extra">
                      {organizationPhone && (
                        <span className="contact-line">
                          <WhatsAppOutlined />
                          <a
                            href={`https://wa.me/${organizationPhone}?text=Hola%20${organization}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            referrerPolicy="origin"
                          >
                            {organizationPhone}
                          </a>
                          <span className="contact-label">(General)</span>
                        </span>
                      )}
                      {instagram && (
                        <span className="contact-line">
                          <InstagramOutlined />
                          <a
                            href={`https://instagram.com/${instagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            @{instagram}
                          </a>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="contact-block">
                  <h4 className="contact-block-title">Contacto</h4>
                  <div className="contact-list">
                    {direction && (
                      <div className="contact-line">
                        <EnvironmentOutlined />
                        <span>{direction}</span>
                      </div>
                    )}
                    {organizationPhone && (
                      <div className="contact-line">
                        <WhatsAppOutlined />
                        <a
                          href={`https://wa.me/${organizationPhone}?text=Hola%20${organization}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          referrerPolicy="origin"
                        >
                          {organizationPhone}
                        </a>
                      </div>
                    )}
                    {instagram && (
                      <div className="contact-line">
                        <InstagramOutlined />
                        <a
                          href={`https://instagram.com/${instagram}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          @{instagram}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {loading ? (
          <Spin />
        ) : (
          <div>
            {/* Profesionales */}
            <div style={{ marginBottom: 50 }} ref={profesionalesRef}>
              <Title level={3}>Nuestro Equipo</Title>
              {Professional({
                professionals: filteredProfessionals,
              })}
            </div>
          </div>
        )}
      </Content>
      <Footer />
    </Layout>
  );
};

export default PortalHome;
